import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Heading,
    Text,
    Divider,
    IconButton,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import Link from '@salesforce/retail-react-app/app/components/link'
import {QRCodeIcon, LeftArrow} from '../../../components/icons'
import Barcode from 'react-barcode'
import {FormattedMessage, FormattedNumber} from 'react-intl'

const StoreOrderPayment = ({paymentData: [payment = {}] = []}) => {
    const lastFourNumbers = payment?.cardNumber?.slice(-4) ?? null

    return (
        <Box fontSize="xs" color="gray.600" py={3}>
            <Text>
                <FormattedMessage
                    id="account_order_detail.label.payment_by_store"
                    defaultMessage="Payment By: {paymentMethod}"
                    values={{paymentMethod: payment.name}}
                />
                {lastFourNumbers && (
                    <>
                        {' '}
                        <FormattedMessage
                            id="account_order_detail.label.ending_card_number_store"
                            defaultMessage="ending {lastFourNumbers}"
                            values={{lastFourNumbers}}
                        />
                    </>
                )}
            </Text>
        </Box>
    )
}

StoreOrderPayment.propTypes = {
    paymentData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            cardNumber: PropTypes.string
        })
    )
}

const StoreOrderSummary = ({orderSubtotal, totalTax, shippingFee, orderTotal, currency}) => {
    // Ensure all numeric values are valid numbers to prevent NaN
    const safeOrderSubtotal = Number(orderSubtotal) || 0
    const safeTotalTax = Number(totalTax) || 0
    const safeShippingFee = Number(shippingFee) || 0
    const safeOrderTotal = Number(orderTotal) || 0

    return (
        <Stack spacing={0} w="full" divider={<Divider borderColor="grey.400" />} fontSize="sm">
            <Box px={4} py={3}>
                <Flex justifyContent="space-between" mb={3}>
                    <Text fontWeight="bold">
                        <FormattedMessage
                            id="account_order_detail.label.subtotal_store"
                            defaultMessage="Subtotal"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={safeOrderSubtotal}
                        />
                    </Text>
                </Flex>
                <Flex justifyContent="space-between" mb={3}>
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.label.order_shipping_fee"
                            defaultMessage="Shipping"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber style="currency" currency={currency} value={safeShippingFee} />
                    </Text>
                </Flex>
                <Flex justifyContent="space-between" mb={3}>
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.label.shipping_discount_store"
                            defaultMessage="Shipping Discount"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={-safeShippingFee}
                        />
                    </Text>
                </Flex>
                <Flex justifyContent="space-between" mb={3}>
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.label.total_sales_tax_store"
                            defaultMessage="Total Sales Tax"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber style="currency" currency={currency} value={safeTotalTax} />
                    </Text>
                </Flex>
            </Box>
            <Box px={4} py={3}>
                <Flex justifyContent="space-between" textTransform="uppercase" fontWeight="bold">
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.label.total_store"
                            defaultMessage="Total"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber style="currency" currency={currency} value={safeOrderTotal} />
                    </Text>
                </Flex>
            </Box>
        </Stack>
    )
}

StoreOrderSummary.propTypes = {
    orderSubtotal: PropTypes.number,
    totalTax: PropTypes.number,
    shippingFee: PropTypes.number,
    orderTotal: PropTypes.number,
    currency: PropTypes.string.isRequired
}

const StoreOrderItems = ({productItems, currency}) => {
    return (
        <Stack px={4} py={3} w="full" spacing={4} mb={4}>
            {(productItems ?? []).map(({productName, quantity, salePrice, sku}, index) => (
                <Box key={`${sku}-${index}`}>
                    <Text pb={2}>{productName}</Text>
                    <Flex gap={4} fontSize="xs">
                        <Text>#{sku}</Text>
                        <Text>
                            <FormattedMessage
                                id="account_order_detail.label.quantity_items_store"
                                defaultMessage="Qty: {quantity}"
                                values={{quantity}}
                            />
                        </Text>
                        <Text ml={8}>
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={salePrice}
                            />
                        </Text>
                    </Flex>
                </Box>
            ))}
        </Stack>
    )
}

StoreOrderItems.propTypes = {
    productItems: PropTypes.arrayOf(
        PropTypes.shape({
            productName: PropTypes.string,
            quantity: PropTypes.number,
            salePrice: PropTypes.number,
            sku: PropTypes.string
        })
    ).isRequired,
    currency: PropTypes.string.isRequired
}

const StoreAddress = ({address1, city, zip}) => {
    if (!address1 && !city && !zip) return null

    return <Text>{`${address1}, ${city}, ${zip}`}</Text>
}

StoreAddress.propTypes = {
    address1: PropTypes.string,
    city: PropTypes.string,
    zip: PropTypes.string
}

const StoreOrderReceipt = ({order, currency}) => {
    const {data: customer} = useCurrentCustomer()

    return (
        <Stack
            spacing={0}
            divider={<Divider borderColor="grey.400" />}
            mt={{base: 0, lg: '64px'}}
            border="1px solid"
            borderColor="storeReceiptBorderColor"
            maxW="300px"
            alignItems="center"
            mx={{base: 'auto', lg: 0}}
        >
            <Box>
                <Text
                    fontSize="xs"
                    color="gray.600"
                    textTransform="uppercase"
                    p="10px 0 3px"
                    textAlign="center"
                >
                    <FormattedMessage
                        id="account_order_detail.label.loyalty_id_store"
                        defaultMessage="Loyalty ID: {loyaltyId}"
                        values={{loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber}}
                    />
                </Text>
            </Box>
            <Box textAlign="center" color="primaryBlack" py={3}>
                <Heading as="h3" fontSize="2xl">
                    <FormattedMessage
                        id="account_order_detail.label.success_store"
                        defaultMessage="Success"
                    />
                </Heading>
                <Text fontSize="md">
                    <FormattedMessage
                        id="account_order_detail.label.great_taste_store"
                        defaultMessage="You've got really great taste."
                    />
                </Text>
            </Box>
            <Box fontSize="sm" textAlign="center" color="gray.600" py={3}>
                <Text>
                    <FormattedMessage
                        id="account_order_detail.label.purchase_date_store"
                        defaultMessage="Purchased on {orderDate}"
                        values={{orderDate: order.orderDate}}
                    />
                </Text>
                <StoreAddress {...order?.items?.[0]?.storeInfo?.address} />
            </Box>
            <Box w="full">
                <StoreOrderItems productItems={order.items} currency={currency} />
                <StoreOrderSummary {...order} currency={currency} />
            </Box>
            <StoreOrderPayment paymentData={order.payment} />
        </Stack>
    )
}

StoreOrderReceipt.propTypes = {
    order: PropTypes.shape({
        orderDate: PropTypes.string,
        items: PropTypes.array,
        orderSubtotal: PropTypes.number,
        totalTax: PropTypes.number,
        shippingFee: PropTypes.number,
        orderTotal: PropTypes.number,
        payment: PropTypes.array,
        storeInfo: PropTypes.object
    }),
    currency: PropTypes.string.isRequired
}

export const StoreOrderDetails = ({order, headingRef, currency}) => {
    const [showBarcode, setShowBarcode] = useState(false)

    return (
        <>
            <Heading
                as="h1"
                fontSize={['lg', '2xl']}
                tabIndex="0"
                ref={headingRef}
                textTransform="uppercase"
                mb={8}
            >
                <Flex alignItems="center" gap="64px">
                    <Link display={{base: 'inline-block', lg: 'none'}} href="/account/orders">
                        <LeftArrow />
                    </Link>
                    <FormattedMessage
                        defaultMessage="Order Details"
                        id="account_order_detail.title.order_details"
                    />
                </Flex>
            </Heading>
            <Stack alignItems="flex-start" fontSize="sm">
                <Box
                    sx={{
                        '.order-qr-code': {
                            width: '100%',
                            height: 'auto',
                            marginTop: '12px'
                        }
                    }}
                >
                    <Text textTransform="uppercase" fontWeight="bold">
                        <FormattedMessage
                            defaultMessage="Order Number"
                            id="account_order_detail.label.order_number_store"
                        />
                    </Text>
                    <Text>
                        {order.orderNo}
                        <IconButton
                            variant="unstyled"
                            display={{base: 'inline-flex', lg: 'none'}}
                            w="20px"
                            h="20px"
                            ml="2"
                            minW={'auto'}
                            onClick={() => setShowBarcode((s) => !s)}
                            icon={<QRCodeIcon
                                w="20px"
                                h="20px"
                            />}
                            aria-label="Show QR Code"
                        />
                    </Text>
                    {/* Mobile only - barcode when toggled */}
                    {showBarcode && (
                        <Box display={{base: 'block', lg: 'none'}}>
                            <Barcode
                                className="order-qr-code"
                                width={5}
                                height={150}
                                displayValue={false}
                                value={order.orderNo}
                                margin={0}
                            />
                        </Box>
                    )}
                </Box>
                <Box>
                    <Text textTransform="uppercase" fontWeight="bold">
                        <FormattedMessage
                            defaultMessage="Store Address"
                            id="account_order_detail.label.store_address_store"
                        />
                    </Text>
                    <StoreAddress {...order?.items?.[0]?.storeInfo?.address} />
                </Box>
                <Box>
                    <Text textTransform="uppercase" fontWeight="bold">
                        <FormattedMessage
                            defaultMessage="Order Date"
                            id="account_order_detail.label.order_date_store"
                        />
                    </Text>
                    <Text>{order.orderDate}</Text>
                </Box>
                <Divider
                    display={{base: 'block', lg: 'none'}}
                    mt={6}
                    mb="50px"
                    borderColor="grey.300"
                />
            </Stack>
            <StoreOrderReceipt order={order} currency={currency} />
        </>
    )
}

StoreOrderDetails.propTypes = {
    order: PropTypes.shape({
        orderNo: PropTypes.string,
        orderDate: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.object),
        payment: PropTypes.array,
        orderSubtotal: PropTypes.number,
        totalTax: PropTypes.number,
        shippingFee: PropTypes.number,
        orderTotal: PropTypes.number,
        storeInfo: PropTypes.object
    }),
    headingRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),
    currency: PropTypes.string.isRequired
}
